import { isAxiosError } from "axios";

/**
 * The Ageless Marketing page is the one admin surface that renders without a session: a
 * practice shares its URL with the web developer who will install their Ageless links and
 * website embed. Everything that decides "is this request allowed through the login gate"
 * lives here so the exemption stays a single, auditable list rather than scattered checks.
 */

/**
 * Paths served without a session. Both the current route and the legacy alias are listed —
 * the alias only redirects, but it must survive the login gate to do so.
 */
const PUBLIC_PATH_PATTERNS = [
  /^\/admin\/platforms\/([^/]+)\/ageless\/marketing\/?$/,
  /^\/admin\/platforms\/([^/]+)\/ageless-marketing\/?$/,
] as const;

/** Query parameter carrying the signed setup token from a shared setup link. */
export const AGELESS_MARKETING_SETUP_TOKEN_PARAM = "setupToken";

/**
 * Returns the platform id when `pathname` is a login-free Ageless Marketing path, else null.
 * Deliberately exact — a sub-path such as `.../marketing/settings` is not public.
 */
export function matchAgelessMarketingPublicPath(pathname: string): string | null {
  for (const pattern of PUBLIC_PATH_PATTERNS) {
    const platformId = pattern.exec(pathname)?.[1];
    if (platformId) {
      return platformId;
    }
  }

  return null;
}

/** Reads the setup token out of a location search string. */
export function readAgelessMarketingSetupToken(search: string): string | null {
  const token = new URLSearchParams(search).get(AGELESS_MARKETING_SETUP_TOKEN_PARAM);
  return token && token.trim().length > 0 ? token : null;
}

/** Builds the shareable setup URL an admin hands to their web developer. */
export function buildAgelessMarketingSetupLink(
  origin: string,
  platformId: string,
  setupToken: string,
): string {
  const url = new URL(`/admin/platforms/${platformId}/ageless/marketing`, origin);
  url.searchParams.set(AGELESS_MARKETING_SETUP_TOKEN_PARAM, setupToken);
  return url.toString();
}

/** The auth scheme's answer to an expired or forged setup token: a 401 challenge. */
export const isRejectedSetupTokenError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 401;
